import React, { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { DimensionModel, RoleResponseModel, UserModel } from "../_core/_model";
import { GetDimension, GetRoles, GetUsers } from "../_core/_request.ts";
import { RoleGroup } from "../components/Roles/_core/_model.ts";
import { GetRoleGroups } from "../components/Roles/_core/_request.ts";
import { AuthContext } from "./AuthContext"; // 1. IMPORT AUTH CONTEXT

// Define the shape of your user object
export interface User {
  id: string;
  name: string;
  email: string;
  // Add other fields as needed
}

// Define the shape of the context
interface AppContextType {
  allUsers: UserModel[];
  setAllUsers: (user: UserModel[]) => void;
  dimensions: DimensionModel[];
  setDimensions: (dims: DimensionModel[]) => void;
  roles: RoleResponseModel[];
  refreshRoles: (roles: RoleResponseModel[], callBack: (roles: RoleResponseModel[]) => void) => void,
  allRoleGroups: RoleGroup[],
  setAllRoleGroups: (groups: RoleGroup[]) => void
}

// Create the context with a fallback (null for now)
const AppContext = createContext<AppContextType | undefined>(undefined);

// Hook for components to use context safely
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error("useAppContext must be used within an AppProvider");
  }
  return context;
};

// Props for provider
interface AppProviderProps {
  children: ReactNode;
}

// Provider component
export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const dimensionStyles = {
    D_RB_BU_SBU: { bg: '#e0f7fa', color: '#006064' },
    D_RB_CUST: { bg: '#fce4ec', color: '#880e4f' },
    D_RB_OMBU: { bg: '#fff3e0', color: '#e65100' },
    D_RB_PROD: { bg: '#f3e5f5', color: '#4a148c' },
    D_RB_SOG: { bg: '#e8f5e9', color: '#1b5e20' }
  };

  // 2. GET AUTHENTICATION STATE
  const { user } = useContext(AuthContext);

  const [allUsers, setAllUsers] = useState<UserModel[]>([]);
  const [dimensions, setDimensions] = useState<DimensionModel[]>([]);
  const [roles, setRoles] = useState<RoleResponseModel[]>([]);
  const [allRoleGroups, setAllRoleGroups] = useState<RoleGroup[]>([]);

  useEffect(() => {
    if (user == null) {
      return;
    }

    GetUsers({ userID: 0, userName: "", email: "", roleID: null, roleGroupID: null, managerUserID: null, appliationAccess: null }).then((x) => {
      let aUsers = x.data;
      aUsers.push({
        userName: "Import Job",
        userID: -1,
        userFirstName: "",
        userLastName: "",
        email: "",
        password: "",
        passwordHash: "",
        role: "",
        product: "",
        status: "",
        createdBy: -1,
        createdAt: "",
        isAdUser: 1,
        applicationAccess: 2
      })
      setAllUsers([...aUsers]);
    });

    GetDimension().then((x) => {
      let d = x.data;
      for (var indx in x.data) {
        if (d[indx]) {
          if (d[indx].color == "" || d[indx].color == null)
            d[indx].color = dimensionStyles[d[indx].dimensionCode as keyof typeof dimensionStyles].color;
          if (d[indx].bg == "" || d[indx].bg == null)
            d[indx].bg = dimensionStyles[d[indx].dimensionCode as keyof typeof dimensionStyles].bg;
        }
      }
      setDimensions([...d]);
    })

    GetRoleGroups({ roleGroupID: null, roleGroupName: "", roleID: null, userID: null }).then((x) => {
      setAllRoleGroups([...x.data]);
    })

    refreshRoles([], (r) => { });

  }, [user]); // 4. ADD DEPENDENCY so it re-runs the moment login is successful

  const refreshRoles = (r: RoleResponseModel[], callBack: (roles: RoleResponseModel[]) => void) => {
    // Also protect the manual refresh function
    if (user == null) return;

    if (r.length == 0) {
      GetRoles({ roleID: null, roleName: "", roleCode: "", dataMapColumn: "", roleGroupID: null, userID: null }).then((x) => {
        setRoles([...x.data]);
        callBack(x.data);
      })
    }
    else
      setRoles([...r]);
  }

  return (
    <AppContext.Provider value={{ allUsers, setAllUsers, dimensions, setDimensions, roles, refreshRoles, allRoleGroups, setAllRoleGroups }}>
      {children}
    </AppContext.Provider>
  );
};